import React, { Component, ReactNode } from 'react';

interface ErrorInfo {
  componentStack: string;
  errorBoundary?: string;
  errorBoundaryStack?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'app' | 'page' | 'component';
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private errorId: string;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.errorId = this.generateErrorId();
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: this.errorId
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    this.logError(error, errorInfo);
    
    // Update state with error info
    this.setState({
      error,
      errorInfo
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private generateErrorId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private logError(error: Error, errorInfo: ErrorInfo) {
    const { level = 'component' } = this.props;
    
    console.group(`🚨 Error Boundary (${level})`);
    console.error('Error:', error);
    console.error('Error Message:', error.message);
    console.error('Stack Trace:', error.stack);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Error ID:', this.state.errorId);
    console.error('Timestamp:', new Date().toISOString());
    console.groupEnd();

    // In production, you might want to send this to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Example: Send to error reporting service
      // errorReportingService.log({
      //   error: error.message,
      //   stack: error.stack,
      //   componentStack: errorInfo.componentStack,
      //   errorId: this.state.errorId,
      //   level,
      //   timestamp: new Date().toISOString(),
      //   userAgent: navigator.userAgent,
      //   url: window.location.href
      // });
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: this.generateErrorId()
    });
  };

  private handleGoHome = () => {
    window.location.href = '/desk';
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Otherwise, render default error UI based on level
      const { level = 'component' } = this.props;

      if (level === 'app') {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full bg-surface rounded-lg shadow-md p-6 mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L4.312 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Something went wrong
                  </h3>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.
              </p>

              <div className="space-y-3">
                <button
                  onClick={this.handleRefresh}
                  className="w-full bg-primary-action text-white py-2 px-4 rounded hover:bg-primary-action-hover transition-colors"
                >
                  Refresh Page
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300 transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>

              <div className="mt-6 p-3 bg-gray-50 rounded text-xs text-gray-500">
                Error ID: {this.state.errorId}
              </div>
            </div>
          </div>
        );
      }

      if (level === 'page') {
        return (
          <div className="min-h-96 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="max-w-sm w-full bg-surface rounded-lg shadow-sm p-6 mx-4">
              <div className="flex items-center mb-4">
                <svg className="h-6 w-6 text-danger mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900">Page Error</h3>
              </div>
              
              <p className="text-gray-600 mb-4">
                This page encountered an error and couldn't load properly.
              </p>

              <div className="space-y-2">
                <button
                  onClick={this.handleRetry}
                  className="w-full bg-primary-action text-white py-2 px-3 rounded text-sm hover:bg-primary-action-hover transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="w-full bg-gray-200 text-gray-800 py-2 px-3 rounded text-sm hover:bg-gray-300 transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>

              <div className="mt-4 text-xs text-gray-400">
                Error ID: {this.state.errorId}
              </div>
            </div>
          </div>
        );
      }

      // Component-level error
      return (
        <div className="p-4 bg-danger-soft border border-danger-soft rounded-lg">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-danger mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-danger mb-1">
                Component Error
              </h4>
              <p className="text-sm text-danger mb-3">
                This section encountered an error and couldn't display properly.
              </p>
              <button
                onClick={this.handleRetry}
                className="text-sm bg-danger-soft text-danger px-3 py-1 rounded hover:bg-danger-soft transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;